import { useState, useEffect } from "react";
import { ChatContainer } from "@/components/chat/ChatContainer";
import { chatService, ChatMessage } from "@/lib/chatService";
import { useSEO } from "@/hooks/useSEO";
import { useI18n } from "@/i18n/i18n";
import { useFloatingChat } from "@/components/FloatingChatContext";

const ChatPage = () => {
  const { t } = useI18n();
  const { messages: floatingMessages, setMessages: setFloatingMessages, setIsMinimized, setHasUnreadMessages } = useFloatingChat();
  const [messages, setMessages] = useState<ChatMessage[]>(floatingMessages);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>("");

  // SEO Configuration
  const seoContent = useSEO('main', 'chat');

  // Sync with floating chat messages on mount
  useEffect(() => {
    if (floatingMessages.length > 0) {
      setMessages(floatingMessages);
    }
    // Close the floating window when on the full chat page
    setIsMinimized(true);
    // Mark messages as read when entering chat page
    setHasUnreadMessages(false);
  }, []);

  // Sync messages back to floating chat context
  useEffect(() => {
    setFloatingMessages(messages);
  }, [messages]);

  const handleSendMessage = async (messageContent: string) => {
    if (!messageContent.trim() || isLoading) return;

    setError("");
    setIsLoading(true);

    // Add user message immediately
    const userMessage = chatService.createUserMessage(messageContent);
    setMessages(prev => [...prev, userMessage]);

    // Create a reference for assistant message ID
    const assistantMessageId = chatService.generateMessageId();
    let messageAdded = false;

    try {
      // Call the chat service with streaming callback and conversation history
      await chatService.sendMessage(messageContent, {}, (chunk: string) => {
        // Only add the assistant message when we receive the first chunk (non-empty content)
        if (!messageAdded && chunk.trim()) {
          const assistantMessage: ChatMessage = {
            id: assistantMessageId,
            role: 'assistant',
            content: chunk,
            timestamp: new Date(),
          };
          setMessages(prev => [...prev, assistantMessage]);
          messageAdded = true;
        } else if (messageAdded) {
          // Update the assistant message content progressively
          setMessages(prev => 
            prev.map(msg => 
              msg.id === assistantMessageId 
                ? { ...msg, content: chunk }
                : msg
            )
          );
        }
      }, messages);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t("chat.errors.unknown");
      setError(errorMessage);
      console.error("Chat error:", err);
      
      // Remove the assistant message on error if it was added
      if (messageAdded) {
        setMessages(prev => prev.filter(msg => msg.id !== assistantMessageId));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      {seoContent}
      <div className="container mx-auto px-4 py-8">
        {/* Header Section */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {t("chat.title")}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {t("chat.subtitle")}
          </p>
        </div>

        {/* Chat Container */}
        <ChatContainer
          messages={messages}
          onSendMessage={handleSendMessage}
          isLoading={isLoading}
          error={error}
        />

        {/* Information Section */}
        <div className="mt-12 max-w-4xl mx-auto">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center p-6 bg-white rounded-lg shadow-sm border border-gray-100">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">💡</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">{t("chat.features.personalized.title")}</h3>
              <p className="text-sm text-gray-600">
                {t("chat.features.personalized.description")}
              </p>
            </div>
            
            <div className="text-center p-6 bg-white rounded-lg shadow-sm border border-gray-100">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">📊</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">{t("chat.features.planning.title")}</h3>
              <p className="text-sm text-gray-600">
                {t("chat.features.planning.description")}
              </p>
            </div>
            
            <div className="text-center p-6 bg-white rounded-lg shadow-sm border border-gray-100">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🔒</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">{t("chat.features.security.title")}</h3>
              <p className="text-sm text-gray-600">
                {t("chat.features.security.description")}
              </p>
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mt-8 max-w-4xl mx-auto">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-800">
              <strong>{t("chat.disclaimer.title")}</strong> {t("chat.disclaimer.content")}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatPage;
